import { AppConstants } from '@/app.constants';
import { Inspection } from '@/types/Inspection.type';
import { PartialInspection } from '@/types/ModuleComponentProp.type';
import { idbStorage } from './idbStorage';
import { InspectionOwner } from '@/enums/InspectionOwner.enum';

// 10 minutes in milliseconds
const LOCK_TTL_MS = 10 * 60 * 1000;

export const savedInspectionsStorageV2 = {

  /**
   * Private helper to check if an operation is allowed based on lock status
   * @param inspectionId - The inspection ID to check
   * @param owner - The owner attempting the operation
   * @returns Object with isAllowed boolean and error message if applicable
   */
  _checkLockPermission: async (inspectionId: string, owner: InspectionOwner): Promise<{ isAllowed: boolean; errorMessage?: string }> => {
    const inspection = await savedInspectionsStorageV2.get(inspectionId);

    if (!inspection?.locked) {
      return { isAllowed: true };
    }

    const lockTime = new Date(inspection.locked.at).getTime();
    const currentTime = new Date().getTime();
    const isExpired = (currentTime - lockTime) > LOCK_TTL_MS;

    // If lock is expired, allow the operation
    if (isExpired) {
      console.log(`Lock for inspection ${inspectionId} has expired, allowing operation`);
      return { isAllowed: true };
    }

    // If lock is still valid and not owned by the requester, deny
    if (inspection.locked.by !== owner) {
      const errorMessage = `Operation denied: inspection ${inspectionId} is locked by ${inspection.locked.by}`;
      console.error(errorMessage);
      return { isAllowed: false, errorMessage };
    }

    return { isAllowed: true };
  },

  delete: async (key: string, owner: InspectionOwner): Promise<boolean> => {
    const { isAllowed } = await savedInspectionsStorageV2._checkLockPermission(key, owner);
    if (!isAllowed) {
      return false;
    }

    await idbStorage.removeItem(AppConstants.INDEX_DB.store.savedInspections, key);
    return true;
  },

  getAllIds: async () => {
    const keys = await idbStorage.getAllKeys(AppConstants.INDEX_DB.store.savedInspections);
    return keys.map(key => key.toString());
  },

  get: async (id: string): Promise<Inspection | null> => {
    try {
      const inspection = await idbStorage.getItem(AppConstants.INDEX_DB.store.savedInspections, id);
      const parsedInspection = inspection ? JSON.parse(inspection) as Inspection : null;
      if (!parsedInspection) return null;

      const isExpired = isLockExpired(parsedInspection);
      if (isExpired) {
        parsedInspection.locked = undefined;
      }
      return parsedInspection;
    } catch (error) {
      console.error(`Failed to get inspection ${id}:`, error);
      // @ts-ignore
      Sentry.captureException(error);
      return null;
    }
  },

  save: async (inspection: Inspection, owner: InspectionOwner): Promise<boolean> => {
    const { isAllowed } = await savedInspectionsStorageV2._checkLockPermission(inspection.id, owner);
    if (!isAllowed) {
      return false;
    }

    await idbStorage.setItem(AppConstants.INDEX_DB.store.savedInspections, inspection.id, JSON.stringify(inspection));
    return true;
  },

  patch: async (inspection: PartialInspection, owner: InspectionOwner): Promise<boolean> => {
    if (!inspection.id) {
      console.error('Cannot patch inspection without ID');
      return false;
    }

    const { isAllowed } = await savedInspectionsStorageV2._checkLockPermission(inspection.id, owner);
    if (!isAllowed) {
      return false;
    }

    let savedInspection = await savedInspectionsStorageV2.get(inspection.id);
    if (savedInspection) {
      savedInspection = { ...savedInspection, ...inspection };
      await idbStorage.setItem(AppConstants.INDEX_DB.store.savedInspections, inspection.id, JSON.stringify(savedInspection));
      return true;
    }
    return false;
  },

  getAll: async (): Promise<Inspection[]> => {
    const inspections = await idbStorage.getAll(AppConstants.INDEX_DB.store.savedInspections);
    return inspections.map(inspection => JSON.parse(inspection) as Inspection);
  },

  hasInspections: async (): Promise<boolean> => {
    return idbStorage.hasRecords(AppConstants.INDEX_DB.store.savedInspections);
  },

  lock: async (inspectionId: string, owner: InspectionOwner): Promise<boolean> => {
    const inspection = await savedInspectionsStorageV2.get(inspectionId);
    if (!inspection) {
      return false;
    }

    // Check if already locked and if the lock has expired
    if (inspection.locked) {
      const isExpired = isLockExpired(inspection);

      // If lock is still valid and owned by someone else, deny
      if (!isExpired && inspection.locked.by !== owner) {
        return false;
      }

      // If lock is expired, we can proceed to lock it
      if (isExpired) {
        console.log(`Lock for inspection ${inspectionId} has expired, allowing new lock`);
      } else {
        if (inspection.locked.by === owner) {
          console.log(`Lock for inspection ${inspectionId} is owned by the requester, extending lock`);
        }
      }
    }

    inspection.locked = {
      at: new Date().toISOString(),
      by: owner,
      ttl: LOCK_TTL_MS
    };

    await idbStorage.setItem(AppConstants.INDEX_DB.store.savedInspections, inspection.id, JSON.stringify(inspection));
    return true;
  },

  unlock: async (inspectionId: string, owner: InspectionOwner): Promise<boolean> => {
    const inspection = await savedInspectionsStorageV2.get(inspectionId);
    if (!inspection || !inspection.locked) {
      return false;
    }

    // Only the lock owner can unlock
    if (inspection.locked.by !== owner) {
      return false;
    }

    delete inspection.locked;
    await idbStorage.setItem(AppConstants.INDEX_DB.store.savedInspections, inspection.id, JSON.stringify(inspection));
    return true;
  },

  isLocked: async (inspectionId: string): Promise<{ locked: boolean; owner?: InspectionOwner; expiresAt?: string }> => {
    const inspection = await savedInspectionsStorageV2.get(inspectionId);
    if (!inspection || !inspection.locked) {
      return { locked: false };
    }

    const lockTime = new Date(inspection.locked.at).getTime();
    const currentTime = new Date().getTime();
    const isExpired = (currentTime - lockTime) > LOCK_TTL_MS;

    if (isExpired) {
      return { locked: false };
    }

    const expiresAt = new Date(lockTime + LOCK_TTL_MS).toISOString();
    return {
      locked: true,
      owner: inspection.locked.by,
      expiresAt
    };
  },
};
function isLockExpired(inspection: Inspection) {
  if (!inspection.locked?.at) return true;

  const lockTime = new Date(inspection.locked.at).getTime();
  const currentTime = new Date().getTime();
  const isExpired = (currentTime - lockTime) > LOCK_TTL_MS;
  return isExpired;
}

